
import { useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Pencil, Trash2 } from "lucide-react";
import { Shell } from "@/components/layout/Shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCurrencies } from "@/hooks/useCurrencies";
import { CurrencyModal } from "@/components/currencies/CurrencyModal";
import { Currency } from "@/lib/types";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

export default function CurrenciesPage() {
  const { t } = useTranslation();
  const { useCurrenciesQuery, useDeleteCurrencyMutation } = useCurrencies();
  const { data: currencies, isLoading, error } = useCurrenciesQuery();
  const deleteCurrency = useDeleteCurrencyMutation();

  const [currencyToDelete, setCurrencyToDelete] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currencyToEdit, setCurrencyToEdit] = useState<Currency | undefined>(undefined);

  const handleDeleteClick = (id: string) => {
    setCurrencyToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (currencyToDelete) {
      await deleteCurrency.mutateAsync(currencyToDelete);
      setCurrencyToDelete(null);
    }
    setIsDeleteDialogOpen(false);
  };

  const handleEditClick = (currency: Currency) => {
    setCurrencyToEdit(currency);
    setIsAddModalOpen(true);
  };

  const handleAddClick = () => {
    setCurrencyToEdit(undefined);
    setIsAddModalOpen(true);
  };

  return (
    <Shell>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('currencies')}</h1>
            <p className="text-muted-foreground">
              {t('manageCurrenciesDescription')}
            </p>
          </div>
          <Button onClick={handleAddClick}>
            <PlusCircle className="mr-2 h-4 w-4" />
            {t('addCurrency')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('yourCurrencies')}</CardTitle>
            <CardDescription>
              {t('currenciesForContracts')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="py-10 text-center">{t('loadingCurrencies')}</div>
            ) : error ? (
              <div className="py-10 text-center text-red-500">{t('errorLoadingCurrencies')}</div>
            ) : currencies && currencies.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('name')}</TableHead>
                    <TableHead>{t('code')}</TableHead>
                    <TableHead>{t('symbol')}</TableHead>
                    <TableHead className="w-[100px]">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currencies.map((currency) => (
                    <TableRow key={currency.id}>
                      <TableCell className="font-medium">{currency.name}</TableCell>
                      <TableCell>{currency.code}</TableCell>
                      <TableCell>{currency.symbol}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEditClick(currency)}
                          >
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">{t('edit')}</span>
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteClick(currency.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                            <span className="sr-only">{t('delete')}</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-10 text-center">
                <p className="text-muted-foreground">{t('noCurrenciesFound')}</p>
                <Button className="mt-4" onClick={handleAddClick}>
                  {t('addFirstCurrency')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CurrencyModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        currency={currencyToEdit}
        onSuccess={() => {
          setCurrencyToEdit(undefined);
        }}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteCurrencyWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-500 hover:bg-red-600">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
